/**
 * Schema-management summary preview + update-config request + finalize
 * routing.
 *
 * `handleUpdateConfigRequest` commits the scheduled/draft config to the API,
 * fanning out to multi-release-version saves when needed.
 *
 * `handleGeneralPreviewUpdate` adopts `runAsyncAction` for the spinner-only
 * pattern.
 */

import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import set from "lodash/set";
import has from "lodash/has";
import { getStore } from "@store";
import getRouterConfig from "@hooks/useRouter";
import { mergeAdvanced } from "object-merge-advanced";
import schemaManagementAdapter from "@features/schema-management/adapters";
import { isObject } from "@utils/input-helpers";
import { arraysAreEqual } from "@utils/array-helpers";
import { isEmptyObject } from "@utils/object-helpers";
import {
  mergeSmVersionChanges,
  configSchedulerEnrichment,
  getModifiedSections,
  logDiffs,
} from "@features/schema-management/utils/schemaManagementHelpers";
import { formatBrcConfig } from "../sypiBrcInterface";
import {
  DISPATCH_ERRORS,
  SCHEDULE_DRAFT_ACTIONS,
  SYF_CARD_ACCESS,
  SYPI_BRC,
} from "@constants/schema-management";
import { STATE_CONTANTS } from "@constants";
import { runAsyncAction } from "@utils/runAsyncAction";
import {
  showSpinner,
  stopSpinner,
  setToastMessage,
  setAlertDock,
  closeDock,
  clearChanges,
  clearLog,
  recordChangeObject,
  logChangedKey,
} from "@actions/global";
import {
  loadNewDefaultsPreview,
  loadSummaryChangesReview,
  loadGeneralSummaryPreview,
  loadSummarySections,
  loadConfig,
  toggleSummaryModal,
  toggleSectionsModal,
  setDeletedClients,
} from "@features/schema-management/actions";
import { getSummaryPreview } from "./sm-shared";

export const handleSummaryPreviewAndRequirements = async (
  scheduledChangesWithCommonKeys,
  successFailedKeysReport,
  store = getStore()
) => {
  const {
    schemaAllView: {
      initialConfig,
      changeDict,
      loggedChanges,
      featureName,
      schemaType,
    },
  } = store.getState();
  const configCopy = cloneDeep(changeDict);
  if (changeDict && !isEmptyObject(changeDict)) {
    const { summaryPreview, successFailedImportedKeysReport } =
      await getSummaryPreview(
        schemaType === "array",
        initialConfig,
        loggedChanges,
        configCopy
      );
    const adjustedSummaryPreview =
      scheduledChangesWithCommonKeys &&
      !isEmptyObject(scheduledChangesWithCommonKeys)
        ? scheduledChangesWithCommonKeys
        : summaryPreview;
    const adjustedSuccessFailedImportedKeysReport =
      successFailedKeysReport && !isEmptyObject(successFailedKeysReport)
        ? successFailedKeysReport
        : successFailedImportedKeysReport;
    await setGeneralPreviewSummary(
      featureName,
      adjustedSummaryPreview,
      initialConfig,
      adjustedSuccessFailedImportedKeysReport
    );
  }
};

/**
 * Deletes the initial draft/scheduled row (if name or live-date changed
 * during editing) and clears form state. NOTE: the original function
 * references a `finalReport` variable that is never declared in the
 * enclosing scope — that dead assignment is preserved to keep behavior
 * byte-for-byte (no throw, no return of that report).
 */
export const cleanUpScheduleDraftChanges = async (
  scheduledDraftResults,
  store = getStore()
) => {
  const { initialScheduleDraft, scheduleDraft } =
    store.getState().schemaConfigScheduler;
  const { featureName, isAthosConfig } = store.getState().schemaAllView;
  // Deferred import breaks cycle (deleteScheduledDraft lives in sm-schedule).
  const { deleteScheduledDraft } = await import("./sm-schedule");
  for (let release_version in scheduledDraftResults) {
    let results = scheduledDraftResults[release_version];
    let { apiError } = results;

    if (apiError && apiError.details) {
      // Preserves original behavior: assigns to an undeclared variable.
      // eslint-disable-next-line no-undef
      finalReport = {
        type: "ALERT_DOCK",
        title: `Saving the ${release_version} draft was unsuccessful. `,
        message:
          "Please contact the application development team for assistance.",
      };
    } else {
      if (
        initialScheduleDraft.status === "draft" &&
        ((initialScheduleDraft.name &&
          initialScheduleDraft.name !== scheduleDraft.name) ||
          initialScheduleDraft.status !== scheduleDraft.status)
      ) {
        await deleteScheduledDraft(
          {
            name: initialScheduleDraft.name,
            release_version:
              initialScheduleDraft.release_version ||
              initialScheduleDraft.releaseVersion,
          },
          SCHEDULE_DRAFT_ACTIONS.DELETE_DRAFT
        );
      } else if (
        initialScheduleDraft.status === "scheduled" &&
        initialScheduleDraft.liveScheduleDate &&
        initialScheduleDraft.liveScheduleDate !==
          scheduleDraft.liveScheduleDate &&
        scheduleDraft.status === "scheduled"
      ) {
        await deleteScheduledDraft(
          {
            liveScheduleDate: initialScheduleDraft.liveScheduleDate,
            name: initialScheduleDraft.name,
            release_version:
              initialScheduleDraft.release_version ||
              initialScheduleDraft.releaseVersion,
          },
          SCHEDULE_DRAFT_ACTIONS.DELETE_SCHEDULED
        );
      }
      if (featureName === SYF_CARD_ACCESS || isAthosConfig)
        store.dispatch(setDeletedClients({}));
      store.dispatch(closeDock());
      store.dispatch(clearChanges());
      store.dispatch(clearLog());
    }
  }
};

export const handleUpdateConfigRequest = async (
  typeOfChange,
  scheduleNewDefaults = false,
  store = getStore()
) => {
  store.dispatch(showSpinner());
  if (scheduleNewDefaults) {
    // Deferred to break the summary→schedule→summary import cycle.
    const { saveScheduleWithSchemaDefaults } = await import("./sm-schedule");
    await saveScheduleWithSchemaDefaults();
  }
  const storeContents = store.getState();
  const {
    schema: { activeReleaseVersion, selectedReleaseVersions },
    schemaAllView: { changeDict, featureName },
    schemaConfigScheduler: { otherConfigsScheduledDrafts },
  } = storeContents;
  const modifiedSections = getModifiedSections(changeDict);
  const isScheduled = typeOfChange === "scheduled";
  const isCommercial = featureName === SYPI_BRC;
  const updatedScheduledDraft = configSchedulerEnrichment(
    storeContents,
    modifiedSections
  );
  let scheduledDraftResults = {};
  if (isScheduled) {
    for (let index in selectedReleaseVersions) {
      let release_version = selectedReleaseVersions[index];
      if (release_version === activeReleaseVersion) {
        const results = await schemaManagementAdapter.saveScheduledConfig(
          updatedScheduledDraft,
          isCommercial
        );
        scheduledDraftResults = {
          ...scheduledDraftResults,
          [release_version]: results,
        };
      } else {
        let { summaryView, config } =
          otherConfigsScheduledDrafts[release_version];
        let hasChanged = false;
        let originalChangeDict = {};

        summaryView.forEach((change) => {
          let oldValue = get(config, change.keyName);
          let newValue = change.newKeyValue;
          if (has(config, change.keyName)) {
            if (
              typeof newValue === typeof oldValue &&
              isObject(newValue) === isObject(oldValue) &&
              newValue !== oldValue
            ) {
              set(config, change.keyName, newValue);
              originalChangeDict[change.keyName] = newValue;
              hasChanged = true;
            }
          }
        });

        let importedConfig = {
          newConfig: config,
          release_version: release_version,
          newSummaryView: summaryView,
          changeDict: originalChangeDict,
        };

        if (hasChanged) {
          const modifiedSections = getModifiedSections(originalChangeDict);
          let newUpdatedScheduled = configSchedulerEnrichment(
            storeContents,
            modifiedSections,
            importedConfig
          );
          let newResults = await schemaManagementAdapter.saveScheduledConfig(
            newUpdatedScheduled,
            isCommercial
          );
          scheduledDraftResults = {
            ...scheduledDraftResults,
            [release_version]: newResults,
          };
        }
      }
    }
  } else {
    scheduledDraftResults = await schemaManagementAdapter.saveDraftedConfig(
      updatedScheduledDraft,
      isCommercial
    );
  }

  store.dispatch(stopSpinner());
  return scheduledDraftResults;
};

export const setGeneralPreviewSummary = async (
  feature,
  changeDict,
  initialConfig,
  excludedKeys = {},
  scheduleNewDefaults = false,
  releaseVersion,
  store = getStore()
) => {
  store.dispatch(showSpinner());
  const { schemaAllView, schemaConfigScheduler } = store.getState();
  const { schemaManagementConfig, importedChanges, isAthosConfig } =
    schemaAllView;

  let clients =
    feature === SYF_CARD_ACCESS
      ? schemaManagementConfig.map((feature) => feature.client)
      : [];

  let inputSourceKeysReport = {};
  if (
    changeDict &&
    !isEmptyObject(changeDict) &&
    importedChanges &&
    !isEmptyObject(importedChanges)
  ) {
    Object.keys(importedChanges).forEach((key) => {
      inputSourceKeysReport[key] = {
        imported:
          changeDict[key] === importedChanges[key] ||
          (!changeDict[key] && !importedChanges[key]) ||
          (changeDict[key]?.length === 0 && importedChanges[key]?.length === 0),
      };
    });
  }
  let additionalDetails = mergeAdvanced(inputSourceKeysReport, excludedKeys);
  let summaryReviewBundle = [
    ...(changeDict && !isEmptyObject(changeDict)
      ? mergeSmVersionChanges(
          changeDict,
          feature,
          initialConfig,
          clients,
          additionalDetails,
          isAthosConfig
        )
      : []),
  ];
  let sectionsToUpdate =
    summaryReviewBundle.length > 0
      ? [
          ...new Set(
            summaryReviewBundle.map((summary) => summary.section.toLowerCase())
          ),
        ]
      : [];
  store.dispatch(loadSummarySections(sectionsToUpdate));
  schemaConfigScheduler.configSchedulerStatus ===
  STATE_CONTANTS.SCHEDULER_STATUS
    ? scheduleNewDefaults
      ? store.dispatch(
          loadNewDefaultsPreview(releaseVersion, summaryReviewBundle)
        )
      : store.dispatch(loadSummaryChangesReview(summaryReviewBundle))
    : store.dispatch(loadGeneralSummaryPreview(summaryReviewBundle));

  store.dispatch(stopSpinner());
};

export const sendFinalReport = (report, store = getStore()) => {
  if (!report) return;
  switch (report.type) {
    case "TOAST_MESSAGE":
      const { history } = getRouterConfig();
      store.dispatch(
        setToastMessage(
          report.message,
          report.redirect && history ? history.push(report.redirect) : null
        )
      );
      break;
    case "ALERT_DOCK":
      store.dispatch(
        setAlertDock({
          title: report.title,
          message: report.message,
        })
      );
    default:
      break;
  }
};

/**
 * Rewrites the general summary bundle to include only the sections the user
 * chose to update. Uses `runAsyncAction` for the shared spinner + error
 * wiring.
 */
export const handleGeneralPreviewUpdate = async (store = getStore()) => {
  await runAsyncAction(
    store,
    async () => {
      store.dispatch(toggleSectionsModal(false));
      const { generalPreview } = store.getState().schemaConfigScheduler;
      const { summaryView, summarySections, summarySectionsToUpdate } =
        generalPreview;
      const updatePreview = !arraysAreEqual(
        summarySections,
        summarySectionsToUpdate
      );
      let newSummaryPreview = summaryView;
      if (updatePreview) {
        newSummaryPreview = newSummaryPreview.filter((summary) =>
          summarySectionsToUpdate.includes(summary.section.toLowerCase())
        );
      }
      store.dispatch(toggleSummaryModal(true));
      store.dispatch(loadGeneralSummaryPreview(newSummaryPreview));
    },
    { errorAction: "update", errorSubject: "general preview" }
  );
};

export const onFormChange = (formData, store = getStore()) => {
  const {
    featureName,
    isAthosConfig,
    initialConfig,
    schemaManagementConfig,
    deletedClients,
  } = store.getState().schemaAllView;
  const { selectedConfigTab } = store.getState().schemaConfigScheduler;
  const { activeReleaseVersion } = store.getState().schema;
  if (
    (selectedConfigTab === activeReleaseVersion &&
      featureName === SYF_CARD_ACCESS) ||
    isAthosConfig ||
    (!isEmptyObject(initialConfig) && !isEmptyObject(schemaManagementConfig))
  ) {
    const formattedConfig =
      featureName === SYPI_BRC
        ? formatBrcConfig(schemaManagementConfig, formData)
        : formData;
    store.dispatch(loadConfig(formattedConfig));
    let loggedDiffs = logDiffs(initialConfig, formData, featureName);

    loggedDiffs = { ...deletedClients, ...loggedDiffs };
    store.dispatch(recordChangeObject(loggedDiffs));
    store.dispatch(logChangedKey(Object.keys(loggedDiffs)));
  }
};
